import currency from 'currency.js';
import { DateTime } from 'luxon';
import React from 'react';
import { DateInput, NumberInput, TextInput } from 'ui-toolkit';
import partsFromDate, { generateDateText } from '../utilities/partsFromDate';
import Currency from '../Currency';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IHasMetaDataModalProps from './IHasMetaDataModalProps';
import IHasMetaDataModalState from './IHasMetaDataModalState';
import IItemProps from './IItemProps';
import ItemMetaDataModal, { IItemMetaDataModalRef } from './ItemMetaDataModal/ItemMetaDataModal';
import SharedItemCells from './SharedItemCells';
import './_InterestPrepayment.scss';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import { transformDateInputToValue } from '../utilities/transformDateInputToValue';
import { IInterestPrepaymentViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { isNil } from 'lodash';
import { convertToFloat } from '../../../ui-toolkit/utilities/convertToFloat';

const AMOUNT_PER_DAY_PRECISION = 4;

const hasValue = (value?: any | null) => value !== undefined && value !== null;

interface IInterestPrepaymentProps
  extends IItemProps,
    IHasItemDataProps<IInterestPrepaymentViewItem>,
    IHasItemEventProps<IInterestPrepaymentViewItem>,
    IHasMetaDataModalProps {}

type IInterestPrepaymentState = IHasMetaDataModalState;

export default class InterestPrepayment extends React.Component<IInterestPrepaymentProps, IInterestPrepaymentState> {
  public state = { expanded: !this.props.disableAutoExpand && this.props.data.new };
  private modal = React.createRef<IItemMetaDataModalRef>();
  private firstInputNode: HTMLInputElement | null = null;

  public render() {
    const { data, onChange, hasEditPermissions } = this.props;
    const fromDate = partsFromDate(data.date_from);
    const toDate = partsFromDate(data.date_to);
    const amountPerDay = data.amount_per_day;

    const generateComputedOnChangeHandler = createOnChangeHandlerGenerator(data, (x: any) =>
      onChange(this.updateComputedValue(x))
    );
    const amountPerDayOnChange = generateComputedOnChangeHandler('amount_per_day');
    const fromOnChangeEvent = generateComputedOnChangeHandler('date_from', transformDateInputToValue);
    const toOnChangeEvent = generateComputedOnChangeHandler('date_to', transformDateInputToValue);
    const payeeOnChangeEvent = generateComputedOnChangeHandler('payee');

    return (
      <div className="interest-prepayment">
        <SharedItemCells {...this.props} readOnly={!hasEditPermissions}>
          <ItemMetaDataModal
            ref={this.modal}
            label={
              <span>
                Prepaid Interest (
                <strong>
                  {isNil(amountPerDay) ? (
                    '__.__'
                  ) : (
                    <Currency value={amountPerDay} decimalScale={AMOUNT_PER_DAY_PRECISION} />
                  )}
                  /day
                </strong>
                {' from '}
                <strong>
                  {generateDateText(fromDate)}
                  <span>-</span>
                  {generateDateText(toDate)}
                </strong>
                )<span>&nbsp;to&nbsp;</span>
                {data.payee}
              </span>
            }
            expanded={this.state.expanded}
            expandedLabel="Prepaid Interest"
            onToggle={this.onModalToggleEvent}
          >
            <div className="interest-prepayment__extra">
              <NumberInput
                className="interest-prepayment__rate"
                getInputRef={this.setFirstInputNode}
                name="amount_per_day"
                mode="currency"
                label="Day Rate"
                prefix="$"
                decimalScale={AMOUNT_PER_DAY_PRECISION}
                onBlur={this.onBlurEvent}
                onChange={amountPerDayOnChange}
                value={data.amount_per_day}
                readOnly={!hasEditPermissions}
                debounceOnValueChange
              />
              <DateInput
                className="interest-prepayment__from"
                label="From Date"
                hintText="Loan fund date"
                value={fromDate ? `${fromDate.year}-${fromDate.month}-${fromDate.date}` : null}
                name="date_from"
                onBlur={this.onBlurEvent}
                onChange={fromOnChangeEvent}
                readOnly={!hasEditPermissions}
                attachPortalInsideContainter={true}
              />
              <DateInput
                className="interest-prepayment__to"
                label="To Date"
                hintText="1st day of next month"
                value={toDate ? `${toDate.year}-${toDate.month}-${toDate.date}` : null}
                name="date_to"
                onBlur={this.onBlurEvent}
                onChange={toOnChangeEvent}
                readOnly={!hasEditPermissions}
                attachPortalInsideContainter={true}
              />
              <TextInput
                className="interest-prepayment__payee"
                label="Payee"
                onBlur={this.onBlurEvent}
                onChange={payeeOnChangeEvent}
                placeholder="Payee"
                value={data.payee}
                readOnly={!hasEditPermissions}
              />
            </div>
          </ItemMetaDataModal>
        </SharedItemCells>
      </div>
    );
  }

  private onBlurEvent = (event: React.FocusEvent) => {
    const node = this.modal.current;
    if (node && event.relatedTarget && !node.modalContains(event.relatedTarget as HTMLElement)) {
      this.setState({
        expanded: false,
      });
    }
  };

  private onModalToggleEvent = (expanded: boolean) => {
    this.setState({ expanded });
    if (this.firstInputNode) {
      this.firstInputNode.focus();
    }
  };

  private setFirstInputNode = (input: HTMLInputElement | null) => {
    this.firstInputNode = input;
  };

  private updateComputedValue = (data: IInterestPrepaymentViewItem): IInterestPrepaymentViewItem => {
    const hasComputedDataFields = hasValue(data.amount_per_day) && hasValue(data.date_from) && hasValue(data.date_to);
    if (hasComputedDataFields) {
      const dateFrom = DateTime.fromJSDate(data.date_from!);
      const dateTo = DateTime.fromJSDate(data.date_to!);
      const diff = dateTo.diff(dateFrom, ['days']).days;
      const amountPerDay = data.amount_per_day;
      const total = currency(amountPerDay || 0, { increment: 0.0001, precision: 4 }).multiply(diff); //precise multiplication
      const rounded = Math.round(total.intValue / 100).toString(); //rounded to 2 decimal points w/ an offset for Math.round
      const buyerAtClosing = convertToFloat(`${rounded.substr(0, rounded.length - 2)}.${rounded.substr(-2)}`); //convert back to float
      return {
        ...data,
        buyer_at_closing: buyerAtClosing,
        dirty: true,
      } as IInterestPrepaymentViewItem;
    }
    return {
      ...data,
      buyer_at_closing: null,
    };
  };
}
